/**
 * The in-app Changelog ("What's new") — one release at a time, its changes
 * grouped by WHERE they live in the portal, each labelled with its KIND:
 *
 *   Area card     (Loan Drilldown, Pipelines & Queues …)   collapsible
 *     Screen      (Documents, Loan Lock …)                 collapsible
 *       [New]      Feature: description          ← one change, inline
 *       [Improved] Feature                       ← several changes
 *                    • description
 *       [Fixed]    Feature: description
 *
 * Inside a screen, rows run New, then Improved, then Fixed. The pill carries
 * the type in words as well as color (green / blue / red). On the All tab the
 * area and screen headers add a colored New / Improved / Fixed breakdown; a
 * type tab filters the rows inside every area and hides empty areas.
 *
 * A change places itself with `section` (the area) / `area` (the screen) /
 * `feature` in changelogData.js. Older entries carry only `title` +
 * `description`: they render under "Other updates" with the title as the
 * label. Areas run A to Z; screens appear in first-seen order.
 *
 * Collapse state is one Set of CLOSED keys — `area`, `area::screen`. It lives
 * in memory and resets per release and per search.
 */
import { useEffect, useMemo, useState } from "react";

import AdminPanelSettingsOutlined from "@mui/icons-material/AdminPanelSettingsOutlined";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import LinkRounded from "@mui/icons-material/LinkRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import NorthEastRounded from "@mui/icons-material/NorthEastRounded";
import { Box, Dialog, IconButton, InputBase, Menu, MenuItem, Tooltip } from "@mui/material";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";

import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { selectedStateSx } from "../../theme/activeStates";
import { adminChip, changeKind, segTrack, slate, status, teal } from "../../theme/colors";
import CHANGELOG from "./changelogData";
import { useWhatsNew } from "./WhatsNewProvider";

/* ── tokens (Lendmarq color system) ─────────────────────────────────────── */
// Page on the admin gray canvas, each change type in a white card, the same
// canvas + card border the Admin > Pricing (DSCR) page uses.
const C = {
  bg: segTrack,
  surface: "#FFFFFF",
  cardLine: "#e5e7eb",
  ink: slate[800],
  muted: slate[500],
  faint: slate[400],
  line: slate[200],
  hover: slate[50],
  focus: teal[300],
};

// The kind of change: a pill on every row (label + color, never color
// alone) and a dot in the header breakdown.
const TYPES = [
  {
    key: "feature",
    label: "New features",
    pill: "New",
    ...changeKind.new,
  },
  {
    key: "improvement",
    label: "Improved / updated",
    pill: "Improved",
    ...changeKind.improved,
  },
  {
    key: "fix",
    label: "Fixed / bugs",
    pill: "Fixed",
    ...changeKind.fixed,
  },
];
const TYPE_BY_KEY = Object.fromEntries(TYPES.map((t) => [t.key, t]));

const FALLBACK_SECTION = "Other Updates";

// Area cards run A to Z. "Other Updates" (entries with no section) is not a
// real part of the portal, so it always comes last.
const compareAreas = (a, b) => {
  if (a === b) return 0;
  if (a === FALLBACK_SECTION) return 1;
  if (b === FALLBACK_SECTION) return -1;
  return a.localeCompare(b, undefined, { sensitivity: "base" });
};

// Area and screen headings are always Title Case. Only first letters are
// raised; nothing is lowered, so "DSCR" or "GUC" survive as written.
const MINOR_WORDS = new Set([
  "a", "an", "and", "as", "at", "by", "for", "in", "of", "on", "or", "the", "to", "vs",
]);
const titleCase = (str) =>
  String(str || "")
    .split(" ")
    .map((w, i) =>
      i > 0 && MINOR_WORDS.has(w.toLowerCase())
        ? w
        : w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(" ");

/* ── helpers ─────────────────────────────────────────────────────────────── */
const isExternal = (url) => /^https?:\/\//i.test(url || "");
const slug = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const safeDate = (iso) => {
  if (!iso) return null;
  try {
    const d = parseISO(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};
const releaseDate = (rel, pattern = "MMMM d, yyyy") => {
  const d = safeDate(rel?.date);
  return d ? format(d, pattern) : rel?.date || "";
};
// Releases are shown and linked by their US date: 9/29/2026 on screen,
// #09-29-2026 in the URL.
const US_DATE = "M/d/yyyy";
const releaseId = (rel) => {
  const d = safeDate(rel?.date);
  return d ? format(d, "MM-dd-yyyy") : slug(rel?.version || rel?.date);
};
// Links shared before the switch carry the version slug (#2026-09-29).
const matchesHash = (rel, hash) =>
  releaseId(rel) === hash || slug(rel?.version) === hash;

/** Groups rows by key, keeping first-seen order. */
const groupBy = (arr, keyFn) => {
  const m = new Map();
  arr.forEach((d) => {
    const k = keyFn(d);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(d);
  });
  return [...m.entries()];
};

/** One change → one row the renderer understands. */
// Who a gated change is for, as the pill says it. A change with no roles of
// its own inherits its release's.
// Admin and super-admin changes both read "Admin" on the chip; the hover says
// who can actually see it. Other roles name their group.
const AUDIENCE_LABEL = {
  capitalMarkets: "Capital Markets",
  closer: "Closers",
  underwriter: "Underwriters",
};
const audienceOf = (roles) => {
  if (!Array.isArray(roles) || !roles.length) return null;
  if (roles.includes("superAdmin") && roles.length === 1) {
    return { kind: "superAdmin", label: "Admin", who: "Super Admin users" };
  }
  if (roles.includes("admin") || roles.includes("superAdmin")) {
    return { kind: "admin", label: "Admin", who: "Admin users" };
  }
  const names = roles.map((r) => AUDIENCE_LABEL[r] || r).join(" & ");
  return { kind: names, label: `${names} only`, who: `${names} users` };
};

/**
 * The chip a GROUP of changes carries — a feature with several changes, a
 * screen, or a whole area like Sizers — when EVERY change under it is gated to
 * the same audience (admin and super admin roll up together as "Admin"). Its
 * children then don't repeat the chip. Null when any change is for everyone.
 */
const rollupAudience = (rows) => {
  const auds = (rows || []).map((r) => r.audience);
  if (!auds.length || auds.some((a) => !a)) return null;
  const admin = auds.every((a) => a.kind === "admin" || a.kind === "superAdmin");
  if (admin) {
    const allSuper = auds.every((a) => a.kind === "superAdmin");
    return {
      kind: allSuper ? "superAdmin" : "admin",
      label: "Admin",
      who: allSuper ? "Super Admin users" : "Admin users",
    };
  }
  return auds.every((a) => a.kind === auds[0].kind) ? auds[0] : null;
};

/** The hover text: one change, or a group and everything nested under it. */
const audienceTip = (aud, nested) =>
  nested
    ? `This item and everything nested under it can only be seen by ${aud.who}.`
    : `Only ${aud.who} can see this change.`;

const toRow = (c, rel) => ({
  audience: audienceOf(c.roles?.length ? c.roles : rel?.roles),
  type: TYPES.some((t) => t.key === c.type) ? c.type : "improvement",
  section: titleCase(c.section || FALLBACK_SECTION),
  area: c.area ? titleCase(c.area) : null,
  feature: c.feature || c.title || null,
  text: c.description || c.text || "",
  links: [
    ...(c.link ? [c.link] : []),
    ...(Array.isArray(c.links) ? c.links : []),
  ],
  images: Array.isArray(c.images) ? c.images : [],
  // Everything a search can hit: where it lives, its label, title and text.
  haystack: [c.section, c.area, c.feature, c.title, c.description, c.text, audienceOf(c.roles)?.label]
    .filter(Boolean)
    .join(" ")
    .toLowerCase(),
});

/** Every word of the query must appear somewhere in the row. */
const matchesQuery = (row, words) => words.every((w) => row.haystack.includes(w));

/* ── atoms ───────────────────────────────────────────────────────────────── */
const Count = ({ n, size = 11.5 }) => (
  <Box
    component="span"
    sx={{
      fontSize: size,
      fontWeight: 500,
      lineHeight: "18px",
      bgcolor: C.hover,
      border: `1px solid ${C.line}`,
      borderRadius: "999px",
      px: "7px",
      color: C.muted,
      letterSpacing: 0,
    }}
  >
    {n}
  </Box>
);

/** The one collapsible header button, used at all three levels. */
const Toggle = ({ open, onClick, children, sx }) => (
  <Box
    component="button"
    type="button"
    aria-expanded={open}
    onClick={onClick}
    sx={{
      all: "unset",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      cursor: "pointer",
      width: "calc(100% + 16px)",
      borderRadius: "8px",
      p: "4px 8px",
      mx: "-8px",
      color: C.ink,
      "&:hover": { bgcolor: C.hover },
      "&:focus-visible": { outline: `2px solid ${C.focus}`, outlineOffset: 2 },
      "& .chev": {
        flex: "none",
        fontSize: 18,
        color: C.muted,
        transition: "transform .18s ease",
      },
      '&[aria-expanded="false"] .chev': { transform: "rotate(-90deg)" },
      "@media (prefers-reduced-motion: reduce)": {
        "& .chev": { transition: "none" },
      },
      ...sx,
    }}
  >
    <ExpandMoreRounded className="chev" />
    {children}
  </Box>
);

const InlineLink = ({ link, onNavigate }) => (
  <Box
    component="button"
    type="button"
    onClick={() => onNavigate(link.url)}
    sx={{
      all: "unset",
      ml: 0.75,
      display: "inline-flex",
      alignItems: "center",
      gap: 0.25,
      color: teal[700],
      fontWeight: 600,
      cursor: "pointer",
      "&:hover": { textDecoration: "underline" },
      "&:focus-visible": { outline: `2px solid ${C.focus}`, outlineOffset: 2 },
    }}
  >
    {link.label || link.url}
    <NorthEastRounded sx={{ fontSize: 13 }} />
  </Box>
);

const Images = ({ images, onOpen }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
    {images.map((img, i) => (
      <Box key={i} sx={{ maxWidth: 260 }}>
        <Box
          component="img"
          src={img.src}
          alt={img.alt || ""}
          loading="lazy"
          onClick={() => onOpen(img)}
          sx={{
            display: "block",
            maxWidth: "100%",
            maxHeight: 150,
            borderRadius: "8px",
            border: `1px solid ${C.line}`,
            cursor: "zoom-in",
            objectFit: "cover",
          }}
        />
        {img.caption ? (
          <Box sx={{ fontSize: 11.5, color: C.muted, mt: 0.5 }}>
            {img.caption}
          </Box>
        ) : null}
      </Box>
    ))}
  </Box>
);

/* ── levels ──────────────────────────────────────────────────────────────── */
/** Wraps every searched word in a highlight. */
const Highlight = ({ text, words }) => {
  if (!words?.length || !text) return text || null;
  const esc = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const parts = String(text).split(new RegExp(`(${esc.join("|")})`, "gi"));
  return parts.map((p, i) =>
    i % 2 ? (
      <Box
        component="mark"
        key={i}
        sx={{ bgcolor: status.warning.bg, color: "inherit", borderRadius: "2px" }}
      >
        {p}
      </Box>
    ) : (
      p
    ),
  );
};

/**
 * "Admin" — marks a change (or a whole group) only some people can see; it is
 * shown to them only, so the chip says it is not on everyone's screen. Pale
 * yellow, the same soft tint as the New / Improved / Fixed pills, and
 * always on the LEFT of what it marks. The hover names who can see it, and on
 * a group says everything nested under it is covered too.
 */
const AudiencePill = ({ audience, nested = false, focusable = true, sx }) => {
  const tip = audienceTip(audience, nested);
  return (
    <Tooltip title={tip} arrow placement="top">
      <Box
        component="span"
        tabIndex={focusable ? 0 : undefined}
        aria-label={tip}
        sx={{
          flex: "none",
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
          verticalAlign: "1px",
          px: "7px",
          fontSize: 10.5,
          fontWeight: 700,
          lineHeight: "18px",
          borderRadius: "999px",
          bgcolor: adminChip.bg,
          color: adminChip.fg,
          whiteSpace: "nowrap",
          cursor: "help",
          "&:focus-visible": { outline: `2px solid ${C.focus}`, outlineOffset: 2 },
          ...sx,
        }}
      >
        <AdminPanelSettingsOutlined sx={{ fontSize: 12 }} />
        {audience.label}
      </Box>
    </Tooltip>
  );
};

// One change's text, plus its links and images. `showAudience` is false when
// a group above it already carries the chip.
const ChangeText = ({ row, words, onNavigate, onOpenImage, showAudience = true }) => (
  <>
    {showAudience && row.audience ? (
      <AudiencePill audience={row.audience} sx={{ mr: "6px" }} />
    ) : null}
    <Highlight text={row.text} words={words} />
    {row.links.map((lnk, j) => (
      <InlineLink key={j} link={lnk} onNavigate={onNavigate} />
    ))}
    {row.images.length ? (
      <Images images={row.images} onOpen={onOpenImage} />
    ) : null}
  </>
);

/** The fixed-width type pill that starts every feature row. */
const TypePill = ({ type }) => {
  const t = TYPE_BY_KEY[type] || TYPE_BY_KEY.improvement;
  return (
    <Box
      component="span"
      sx={{
        flex: "none",
        width: 68,
        textAlign: "center",
        fontSize: 11,
        fontWeight: 600,
        lineHeight: "20px",
        borderRadius: "999px",
        bgcolor: t.bg,
        color: t.fg,
        mt: "1px",
      }}
    >
      {t.pill}
    </Box>
  );
};

/** "● 15 ● 12 ● 3" — the New / Improved / Fixed mix of a group. */
const TypeMix = ({ rows }) => (
  <Box
    component="span"
    sx={{ display: { xs: "none", sm: "inline-flex" }, gap: "8px", ml: "2px" }}
  >
    {TYPES.map((t) => {
      const n = rows.filter((r) => r.type === t.key).length;
      return n ? (
        <Box
          key={t.key}
          component="span"
          title={`${n} ${t.label.toLowerCase()}`}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            fontSize: 12,
            fontWeight: 500,
            color: C.muted,
          }}
        >
          <Box
            component="span"
            sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: t.dot }}
          />
          {n}
        </Box>
      ) : null;
    })}
  </Box>
);

const DOT = { left: 8, top: 14, width: 5, height: 5, borderRadius: "50%" };
const listSx = { m: 0, p: 0, listStyle: "none" };
const rowHoverSx = { borderRadius: "6px", "&:hover": { bgcolor: C.hover } };

/**
 * The feature rows of one screen, New first, then Improved, then Fixed. Each
 * row starts with its type pill and a bold label. A feature with ONE change
 * reads inline ("Layout: Each release's changes…"); with several, the label
 * stands alone and its changes list under it as dots. The same feature can
 * appear twice when it has changes of two types.
 */
const Features = ({ rows, words, covered = false, ...rest }) => {
  const sorted = TYPES.flatMap((t) => rows.filter((r) => r.type === t.key));
  const groups = groupBy(sorted, (r) => `${r.type}::${r.feature || ""}`);
  return (
    <Box component="ul" sx={{ ...listSx, my: "6px" }}>
      {groups.map(([key, items]) => {
        const { type, feature } = items[0];
        const single = items.length === 1;
        // The group carries the chip when every change in it is gated (a
        // single change is its own group); its changes then don't repeat it.
        const groupAud = covered ? null : rollupAudience(items);
        return (
          <Box
            component="li"
            key={key}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "flex-start" },
              gap: { xs: "4px", sm: "10px" },
              p: "5px 8px",
              mx: "-8px",
              fontSize: 14,
              lineHeight: 1.55,
              color: changeKind.text,
              ...(single ? rowHoverSx : null),
            }}
          >
            {groupAud ? (
              <AudiencePill audience={groupAud} nested={!single} sx={{ mt: "1px" }} />
            ) : null}
            <TypePill type={type} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {feature ? (
                <Box component="strong" sx={{ fontWeight: 600, color: slate[800] }}>
                  <Highlight text={feature} words={words} />
                  {single ? ":" : null}
                </Box>
              ) : null}
              {single ? (
                <>
                  {feature ? " " : null}
                  <ChangeText
                    row={items[0]}
                    words={words}
                    showAudience={false}
                    {...rest}
                  />
                </>
              ) : (
                <Box component="ul" sx={{ ...listSx, mt: "2px" }}>
                  {items.map((r, i) => (
                    <Box
                      component="li"
                      key={i}
                      sx={{
                        position: "relative",
                        p: "4px 8px 4px 20px",
                        mx: 0,
                        ...rowHoverSx,
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          bgcolor: C.faint,
                          ...DOT,
                          top: 13,
                        },
                      }}
                    >
                      <ChangeText
                        row={r}
                        words={words}
                        showAudience={!covered && !groupAud}
                        {...rest}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * An open group's body, with a tree guide line running down from under its
 * chevron (the chevron's centre sits 9px in from the group's left edge).
 * The line is also a hit target: clicking it collapses the group, the way the
 * Documents table's tree guides do. It turns teal on hover as the cue.
 */
const GuideBody = ({ onCollapse, sx, children }) => (
  <Box sx={{ position: "relative", ...sx }}>
    <Box
      aria-hidden
      onClick={onCollapse}
      title="Collapse"
      sx={{
        position: "absolute",
        left: 4,
        top: 2,
        bottom: 2,
        width: 10,
        cursor: "pointer",
        "&::before": {
          content: '""',
          position: "absolute",
          left: "4.5px",
          top: 0,
          bottom: 0,
          width: "1px",
          bgcolor: C.line,
          transition: "background-color .15s ease",
        },
        "&:hover::before": { bgcolor: teal[500] },
      }}
    />
    {children}
  </Box>
);

/** The screens inside one area card. */
const Screens = ({ section, rows, isOpen, flip, showMix, covered = false, ...rest }) => {
  // An area whose changes name no screen lists its features directly.
  if (rows.every((r) => !r.area)) return <Features rows={rows} covered={covered} {...rest} />;
  return groupBy(rows, (r) => r.area || "General").map(([area, items], i) => {
    const key = `${section}::${area}`;
    const open = isOpen(key);
    const areaAud = covered ? null : rollupAudience(items);
    return (
      // Collapsed screens stack tight; an OPEN screen carries the air below
      // its list (the body's bottom padding), so space follows content.
      <Box key={area} sx={{ mt: i === 0 ? "10px" : "4px" }}>
        <Toggle open={open} onClick={() => flip(key)}>
          {areaAud ? <AudiencePill audience={areaAud} nested focusable={false} /> : null}
          <Box component="span" sx={{ fontSize: 16, fontWeight: 600 }}>
            <Highlight text={area} words={rest.words} />
          </Box>
          <Count n={items.length} size={12} />
          {showMix ? <TypeMix rows={items} /> : null}
        </Toggle>
        {open ? (
          <GuideBody
            onCollapse={() => flip(key)}
            sx={{ pl: { xs: "22px", sm: "36px" }, pb: "18px" }}
          >
            <Features rows={items} covered={covered || !!areaAud} {...rest} />
          </GuideBody>
        ) : null}
      </Box>
    );
  });
};

/** One card per area of the portal (Loan Drilldown, Pipelines & Queues…). */
const AreaCard = ({ section, rows, isOpen, flip, showMix, ...rest }) => {
  const open = isOpen(section);
  const sectionAud = rollupAudience(rows);
  return (
    <Box
      component="section"
      // Even top and bottom, so a collapsed card is balanced. Border, radius
      // and shadow belong to the CardStack around every card.
      sx={{ p: { xs: "8px 12px", sm: "10px 20px" } }}
    >
      <Box
        component="h2"
        sx={{ m: 0, fontSize: 20, fontWeight: 700, letterSpacing: "-.015em" }}
      >
        <Toggle open={open} onClick={() => flip(section)} sx={{ font: "inherit" }}>
          {sectionAud ? (
            <AudiencePill audience={sectionAud} nested focusable={false} />
          ) : null}
          <Highlight text={section} words={rest.words} />
          <Count n={rows.length} />
          {showMix ? <TypeMix rows={rows} /> : null}
        </Toggle>
      </Box>
      {open ? (
        <GuideBody
          onCollapse={() => flip(section)}
          sx={{ pl: { xs: "18px", sm: "28px" }, mt: "4px", pb: "6px" }}
        >
          <Screens
            section={section}
            rows={rows}
            isOpen={isOpen}
            flip={flip}
            showMix={showMix}
            covered={!!sectionAud}
            {...rest}
          />
        </GuideBody>
      ) : null}
    </Box>
  );
};

/**
 * The area cards as one block: no gaps, a hairline between cards, rounded
 * only at the top of the first and the bottom of the last, and one hover
 * shadow around the whole stack.
 */
const CardStack = ({ children }) => (
  <Box
    sx={{
      mt: "16px",
      bgcolor: C.surface,
      border: `1px solid ${C.cardLine}`,
      borderRadius: "10px",
      overflow: "hidden",
      transition: "box-shadow .2s",
      "&:hover": { boxShadow: "0 4px 16px -2px rgba(22,33,46,.08)" },
      "& > section + section": { borderTop: `1px solid ${C.cardLine}` },
    }}
  >
    {children}
  </Box>
);

/* ── screen ──────────────────────────────────────────────────────────────── */
const ChangelogScreen = () => {
  useDocumentTitle("Changelog");
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin, isCapitalMarkets, isCloser, isUnderwriter } =
    usePermissionsHook();
  const { canView: canViewWhatsNew, openWhatsNew } = useWhatsNew();
  const [lightbox, setLightbox] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [copied, setCopied] = useState(false);
  const [query, setQuery] = useState("");
  const words = useMemo(
    () => query.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [query],
  );
  // A new search opens everything, so a match is never hidden in a fold.
  useEffect(() => setCollapsed(new Set()), [words.join(" ")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Role gating: a release or a single change may carry `roles`; none → everyone.
  const releases = useMemo(() => {
    const flags = {
      superAdmin: isSuperAdmin,
      admin: isAdmin,
      capitalMarkets: isCapitalMarkets,
      closer: isCloser,
      underwriter: isUnderwriter,
    };
    // Super admins see everything (they bypass every permission).
    const allowed = (roles) =>
      isSuperAdmin ||
      !Array.isArray(roles) ||
      !roles.length ||
      roles.some((r) => flags[r]);
    return (Array.isArray(CHANGELOG) ? CHANGELOG : [])
      // `hidden: true` keeps a release off this page (it is still in the data
      // and still shown in the super-admin Admin Deployment Review).
      .filter((rel) => !rel.hidden && allowed(rel.roles))
      .map((rel) => ({
        ...rel,
        rows: (rel.changes || [])
          .filter((c) => allowed(c.roles))
          .map((c) => toRow(c, rel)),
      }))
      .filter((rel) => rel.rows.length > 0);
  }, [isAdmin, isSuperAdmin, isCapitalMarkets, isCloser, isUnderwriter]);

  // The release in the URL hash (a shared link), else the latest.
  const [selectedId, setSelectedId] = useState(() =>
    window.location.hash.replace(/^#/, ""),
  );
  const release =
    releases.find((r) => matchesHash(r, selectedId)) || releases[0] || null;
  const isLatest = release && release === releases[0];

  // A new release starts fully open on the All tab.
  useEffect(() => {
    setCollapsed(new Set());
    setActiveTab("all");
  }, [release?.version, release?.date]);

  const pickRelease = (rel) => {
    const id = releaseId(rel);
    setSelectedId(id);
    setMenuAnchor(null);
    window.history.replaceState(null, "", `#${id}`);
  };

  const onNavigate = (url) => {
    if (isExternal(url)) window.open(url, "_blank", "noopener,noreferrer");
    else navigate(url);
  };

  const onCopy = () => {
    const url = `${window.location.origin}${window.location.pathname}#${releaseId(release)}`;
    try {
      navigator.clipboard?.writeText(url);
    } catch (e) {
      /* clipboard unavailable */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const allRows = release?.rows || [];
  const rows = words.length
    ? allRows.filter((r) => matchesQuery(r, words))
    : allRows;
  // While searching, say which OTHER releases also have matches.
  const elsewhere = words.length
    ? releases
        .filter((rel) => rel !== release)
        .map((rel) => ({
          rel,
          n: rel.rows.filter((r) => matchesQuery(r, words)).length,
        }))
        .filter((x) => x.n > 0)
    : [];
  const byType = (k) => rows.filter((r) => r.type === k);
  // The tab filters the rows; areas and screens with none left disappear.
  const shownRows = activeTab === "all" ? rows : byType(activeTab);
  const areas = groupBy(shownRows, (r) => r.section)
    .map(([section, items]) => ({ section, items }))
    .sort((x, y) => compareAreas(x.section, y.section));

  const isOpen = (key) => !collapsed.has(key);
  const flip = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // "Collapse all" while anything visible is open, else "Expand all".
  const anyOpen = areas.some((a) => isOpen(a.section));
  const toggleAll = () => {
    if (!anyOpen) return setCollapsed(new Set());
    const keys = new Set(collapsed);
    shownRows.forEach((r) => {
      keys.add(r.section);
      if (r.area) keys.add(`${r.section}::${r.area}`);
    });
    setCollapsed(keys);
  };

  const tabs = [
    { key: "all", label: "All", n: rows.length },
    ...TYPES.map((t) => ({ key: t.key, label: t.label, n: byType(t.key).length })),
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        bgcolor: C.bg,
        color: C.ink,
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          p: { xs: "20px 16px 40px", sm: "28px 32px 48px" },
        }}
      >
        {/* header */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: "8px 16px",
            mb: "20px",
          }}
        >
          <Box>
            <Box
              component="h1"
              sx={{ m: 0, fontSize: 22, fontWeight: 700, letterSpacing: "-.01em" }}
            >
              Changelog
            </Box>
            <Box sx={{ mt: "2px", color: C.muted }}>
              Every change in this release, grouped by where it lives in the portal.
            </Box>
          </Box>
          {release ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Reopens the What's new pop-up for the release on screen.
                  Super admins only, like the pop-up itself. */}
              {canViewWhatsNew ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => openWhatsNew(release)}
                  title="Opens the Admin Deployment Review. Only super admins see it."
                  sx={{
                    all: "unset",
                    display: "inline-flex",
                    alignItems: "center",
                    fontSize: 13,
                    color: C.muted,
                    bgcolor: C.surface,
                    border: `1px solid ${C.line}`,
                    borderRadius: "999px",
                    p: "3px 10px",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    "&:hover": { bgcolor: C.hover, color: C.ink },
                    "&:focus-visible": {
                      outline: `2px solid ${C.focus}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <AdminPanelSettingsOutlined sx={{ fontSize: 16, mr: 0.5 }} />
                  Admin Deployment Review
                </Box>
              ) : null}
              <Box
                component="button"
                type="button"
                aria-haspopup="menu"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{
                  all: "unset",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: 13,
                  color: C.muted,
                  bgcolor: C.surface,
                  border: `1px solid ${C.line}`,
                  borderRadius: "999px",
                  p: "3px 6px 3px 10px",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  "&:hover": { bgcolor: C.hover, color: C.ink },
                  "&:focus-visible": {
                    outline: `2px solid ${C.focus}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {releaseDate(release, US_DATE)}
                {isLatest ? (
                  <Box
                    component="span"
                    sx={{ color: teal[700], fontWeight: 600, ml: 0.5 }}
                  >
                    Latest
                  </Box>
                ) : null}
                <ExpandMoreRounded sx={{ fontSize: 16 }} />
              </Box>
              <IconButton
                size="small"
                onClick={onCopy}
                title={copied ? "Link copied" : "Copy a link to this release"}
                sx={{ color: copied ? teal[700] : C.muted }}
              >
                <LinkRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          ) : null}
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          slotProps={{ paper: { sx: { maxHeight: 420, width: 380 } } }}
        >
          {releases.map((rel, i) => (
            <MenuItem
              key={releaseId(rel) || i}
              selected={rel === release}
              onClick={() => pickRelease(rel)}
              sx={{ display: "block", py: 1, whiteSpace: "normal" }}
            >
              <Box sx={{ fontSize: 13, fontWeight: 600, color: C.ink }}>
                {releaseDate(rel, US_DATE)}
                {i === 0 ? (
                  <Box component="span" sx={{ color: teal[700], ml: 1 }}>
                    Latest
                  </Box>
                ) : null}
              </Box>
              {rel.title ? (
                <Box
                  sx={{
                    fontSize: 12,
                    color: C.muted,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {rel.title}
                </Box>
              ) : null}
            </MenuItem>
          ))}
        </Menu>

        {!release ? (
          <Box sx={{ color: C.muted, py: 6, textAlign: "center" }}>
            No updates to show yet.
          </Box>
        ) : (
          <>
            {/* tabs + collapse all */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "flex-end",
                gap: "0 12px",
                borderBottom: `1px solid ${C.line}`,
                mb: "8px",
              }}
            >
              <Box
                role="tablist"
                sx={{ display: "flex", flexWrap: "wrap", gap: "4px", flex: 1, minWidth: 0 }}
              >
                {tabs.map((t) => (
                  <Box
                    key={t.key}
                    component="button"
                    type="button"
                    role="tab"
                    aria-selected={activeTab === t.key}
                    onClick={() => setActiveTab(t.key)}
                    sx={{
                      all: "unset",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      p: "10px 12px",
                      mb: "-1px",
                      cursor: "pointer",
                      color: C.muted,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      borderBottom: "2px solid transparent",
                      "&:hover": { color: C.ink },
                      "&:focus-visible": {
                        outline: `2px solid ${C.focus}`,
                        outlineOffset: -2,
                      },
                      '&[aria-selected="true"]': selectedStateSx({
                        level: 2,
                        variant: "underline",
                      }),
                    }}
                  >
                    {t.label}
                    <Count n={t.n} />
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  alignSelf: "center",
                  width: { xs: 160, sm: 240 },
                  height: 32,
                  px: "10px",
                  mb: "4px",
                  bgcolor: C.surface,
                  border: `1px solid ${C.line}`,
                  borderRadius: "8px",
                  "&:focus-within": { borderColor: teal[600] },
                }}
              >
                <SearchRounded sx={{ fontSize: 16, color: C.faint }} />
                <InputBase
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Escape" && setQuery("")}
                  placeholder="Search changes"
                  inputProps={{ "aria-label": "Search changes" }}
                  sx={{ flex: 1, fontSize: 13, color: C.ink }}
                />
                {query ? (
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setQuery("")}
                    sx={{ p: "2px", color: C.faint }}
                  >
                    <CloseRounded sx={{ fontSize: 15 }} />
                  </IconButton>
                ) : null}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={toggleAll}
                sx={{
                  all: "unset",
                  p: "10px 4px",
                  fontSize: 13,
                  fontWeight: 500,
                  color: C.muted,
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  "&:hover": { color: C.ink, textDecoration: "underline" },
                  "&:focus-visible": {
                    outline: `2px solid ${C.focus}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {anyOpen ? "Collapse all" : "Expand all"}
              </Box>
            </Box>

            {elsewhere.length ? (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "4px 10px",
                  mt: "10px",
                  fontSize: 13,
                  color: C.muted,
                }}
              >
                Also found in
                {elsewhere.map(({ rel, n }) => (
                  <Box
                    key={releaseId(rel)}
                    component="button"
                    type="button"
                    onClick={() => pickRelease(rel)}
                    sx={{
                      all: "unset",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      cursor: "pointer",
                      color: teal[700],
                      fontWeight: 500,
                      "&:hover": { textDecoration: "underline" },
                      "&:focus-visible": {
                        outline: `2px solid ${C.focus}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    {releaseDate(rel, US_DATE)}
                    <Count n={n} />
                  </Box>
                ))}
              </Box>
            ) : null}

            {words.length && !shownRows.length ? (
              <Box sx={{ color: C.muted, py: 6, textAlign: "center" }}>
                Nothing in this release matches &ldquo;{query.trim()}&rdquo;.
              </Box>
            ) : null}

            {areas.length ? (
              <CardStack>
                {areas.map(({ section, items }) => (
                  <AreaCard
                    key={section}
                    section={section}
                    rows={items}
                    isOpen={isOpen}
                    flip={flip}
                    showMix={activeTab === "all"}
                    words={words}
                    onNavigate={onNavigate}
                    onOpenImage={setLightbox}
                  />
                ))}
              </CardStack>
            ) : null}
          </>
        )}
      </Box>

      {/* image lightbox */}
      <Dialog
        open={Boolean(lightbox)}
        onClose={() => setLightbox(null)}
        maxWidth="lg"
        slotProps={{
          paper: { sx: { background: "transparent", boxShadow: "none", m: 2 } },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setLightbox(null)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseRounded />
          </IconButton>
          {lightbox ? (
            <Box
              component="img"
              src={lightbox.src}
              alt={lightbox.alt || ""}
              sx={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "85vh",
                borderRadius: "10px",
              }}
            />
          ) : null}
        </Box>
      </Dialog>
    </Box>
  );
};

export default ChangelogScreen;
